import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import {
  ApplyCalendarLayoutAssignmentsOutDTO,
  CalendarLayoutDefaultOutDTO,
  ArchiveCalendarLayoutModelResultOutDTO,
  DeleteCalendarLayoutModelResultOutDTO,
  CalendarLayoutModelFilter,
  CalendarLayoutModelRowOutDTO,
  Pageable,
  PageResponseCalendarLayoutModelRowOutDTO,
} from '@api/ringil-api.v2';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, MassActionConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDialog from '@components/framework/dialog/RingilDialog.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { Link } from 'react-router-dom';
import { InputTextarea } from 'primereact/inputtextarea';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { BUILT_IN_LAYOUT_CONFIG } from '../defaults';
import { configFromPayload, payloadFromConfig } from '../payload';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import { CalendarLayoutModelMenu } from './CalendarLayoutModelMenu';
import { useAdminCompanyOptions } from './useAdminCompanyOptions';
import { PICKER_PANEL_CLASS, truncatedValueTemplate, wrappingItemTemplate } from './pickerProps';
import { ApplyModelDialog } from './ApplyModelDialog';

/** Delete and archive report the same per-model shape; only delete counts what it removed. */
type ImpactResult = DeleteCalendarLayoutModelResultOutDTO | ArchiveCalendarLayoutModelResultOutDTO;

interface CalendarLayoutModelsTableProps {
  /** Admin mode exposes system-wide authoring and the system default; company mode does not. */
  admin: boolean;
  onEdit: (row: CalendarLayoutModelRowOutDTO) => void;
  /** Route to a model's page, so its name can be a real link — the parent owns admin vs customer. */
  modelHref: (row: CalendarLayoutModelRowOutDTO) => string;
  /** Bumped by the parent after an external change (a publish, an assignment) to force a refresh. */
  refreshToken?: number;
  /**
   * Bumped to open the create dialog. The button lives in the page header, next to the heading, so
   * the trigger and the dialog sit in different components.
   */
  createSignal?: number;
  /** Called after this table assigns a model, so the assignments tab reflects it. */
  onChanged?: () => void;
}

interface ModelFormState {
  name: string;
  description: string;
  /**
   * Which company owns the model, admin only. Null means system-wide: the two are exclusive, so the
   * dialog asks the question once instead of offering a flag and an owner that can contradict.
   */
  ownerCompanyId: string | null;
  /** Set when the dialog is duplicating an existing model rather than creating an empty one. */
  cloneOf: CalendarLayoutModelRowOutDTO | null;
}

const emptyForm = (): ModelFormState => ({ name: '', description: '', ownerCompanyId: null, cloneOf: null });

export const CalendarLayoutModelsTable: React.FC<CalendarLayoutModelsTableProps> = ({ admin, onEdit, modelHref, refreshToken, createSignal, onChanged }) => {
  const { tr } = useTranslationLgs();
  const { listCalendarLayoutModels, createCalendarLayoutModel, getCalendarLayoutModel, updateCalendarLayoutModel, deleteCalendarLayoutModels, archiveCalendarLayoutModels, setDefault, getDefault } = useContext(ApiContext);

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: CalendarLayoutModelRowOutDTO[]; clear: () => void }>(null);
  const [form, setForm] = useState<ModelFormState | null>(null);
  const [renaming, setRenaming] = useState<CalendarLayoutModelRowOutDTO | null>(null);
  /**
   * The outcome of the last bulk delete / archive: one entry per model, each with the scopes it
   * affected. Both operations report the same shape, apart from delete's removed-assignment count.
   */
  const [impact, setImpact] = useState<{ kind: 'delete' | 'archive'; results: ImpactResult[] } | null>(null);
  const [applying, setApplying] = useState<CalendarLayoutModelRowOutDTO | null>(null);
  /** Rows queued for deletion — one from a row action, several from the mass action. */
  const [deleting, setDeleting] = useState<CalendarLayoutModelRowOutDTO[]>([]);
  const [applyResults, setApplyResults] = useState<ApplyCalendarLayoutAssignmentsOutDTO | null>(null);
  const [currentDefault, setCurrentDefault] = useState<CalendarLayoutDefaultOutDTO | null>(null);
  const [saving, setSaving] = useState(false);
  // Feeds both the owner filter and the create/duplicate dialog's owner picker. The filter bar
  // cannot ask for more pages as it scrolls, so this one fills itself in the background.
  const { options: companyOptions } = useAdminCompanyOptions(admin, 'all');

  const reload = () => tableRef.current?.reloadData();

  const loadData = (filter: CalendarLayoutModelFilter, pageable: Pageable, onSuccess: (paged: PagedData<CalendarLayoutModelRowOutDTO>) => void) => {
    // Which model is the system default is not part of the row, so it is fetched alongside the page
    // and matched by id — one extra call per page rather than a column the backend does not send.
    if (admin) getDefault(setCurrentDefault, undefined, { onError: () => setCurrentDefault(null) });
    listCalendarLayoutModels(pageable, filter, (response: PageResponseCalendarLayoutModelRowOutDTO) => {
      onSuccess({ data: response.data, total: response.total, page: response.page, size: response.size });
    });
  };

  const openCreate = () => setForm(emptyForm());

  // Deliberately seeded at 0 rather than from the prop: pressing the header button on the other tab
  // switches here and bumps the signal in one go, so the value is already raised by the time this
  // table first mounts. Comparing against the prop's initial value would swallow exactly that case.
  const handledCreateSignal = useRef(0);
  useEffect(() => {
    if (!createSignal || createSignal === handledCreateSignal.current) return;
    handledCreateSignal.current = createSignal;
    openCreate();
  }, [createSignal]);

  const openDuplicate = (row: CalendarLayoutModelRowOutDTO) =>
    setForm({
      name: tr('CalendarLayout.copyOf', 'Copy of {{name}}', { name: row.name }),
      description: row.description ?? '',
      // A copy starts under the same owner as the model it came from, which for a system-wide
      // source means system-wide again — retarget it with the picker if that is not wanted.
      ownerCompanyId: row.ownerCompanyId ?? null,
      cloneOf: row,
    });

  const submitForm = () => {
    if (!form || !form.name.trim()) return;
    setSaving(true);
    const create = (payload: ReturnType<typeof payloadFromConfig>) =>
      createCalendarLayoutModel(
        {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          // A customer never owns this decision: the backend puts the model under their company.
          systemWide: admin && !form.ownerCompanyId,
          ownerCompanyId: admin ? (form.ownerCompanyId ?? undefined) : undefined,
          payload,
        },
        detail => {
          setSaving(false);
          setForm(null);
          reload();
          // Straight into the designer: a model without a layout is not useful, and this is the
          // step the user was actually after.
          onEdit({ ...detail, published: !!detail.publishedAt, hasDraft: !!detail.draftPayload } as CalendarLayoutModelRowOutDTO);
        },
        undefined,
        { onError: () => setSaving(false) },
      );

    if (form.cloneOf) {
      // The copy has to look exactly like the source does to whoever asked for it, so it takes the
      // draft when there is one and the published layout otherwise — the same "what am I editing"
      // rule the designer applies (`useCalendarLayoutModel.configOf`).
      getCalendarLayoutModel(form.cloneOf.id, source =>
        create(payloadFromConfig(configFromPayload(source.draftPayload) ?? configFromPayload(source.publishedPayload) ?? BUILT_IN_LAYOUT_CONFIG)),
      );
    } else {
      create(payloadFromConfig(BUILT_IN_LAYOUT_CONFIG));
    }
  };

  /**
   * Deletes every queued model in one call.
   *
   * Deleting is no longer refused for a model in use: the backend removes its assignments and
   * reports, per model, how many went and what each affected scope falls back to. That report is
   * the point of the results dialog — the scopes involved silently change what they render.
   */
  const confirmDelete = () => {
    if (deleting.length === 0) return;
    setSaving(true);
    deleteCalendarLayoutModels(
      { modelIds: deleting.map(row => row.id) },
      data => {
        setSaving(false);
        setDeleting([]);
        tableRef.current?.clear();
        reload();
        onChanged?.();
        setImpact({ kind: 'delete', results: data.results ?? [] });
      },
      undefined,
      { onError: () => setSaving(false) },
    );
  };

  /** Archiving is bulk too, and reports the same per-model impact. */
  const archiveModels = (rows: CalendarLayoutModelRowOutDTO[]) => {
    if (rows.length === 0) return;
    setSaving(true);
    archiveCalendarLayoutModels(
      { modelIds: rows.map(row => row.id) },
      data => {
        setSaving(false);
        tableRef.current?.clear();
        reload();
        onChanged?.();
        setImpact({ kind: 'archive', results: data.results ?? [] });
      },
      undefined,
      { onError: () => setSaving(false) },
    );
  };

  const submitRename = () => {
    if (!renaming || !renaming.name.trim()) return;
    setSaving(true);
    updateCalendarLayoutModel(
      renaming.id,
      { name: renaming.name.trim(), description: renaming.description?.trim() || undefined, calendarLayoutStatus: renaming.calendarLayoutStatus },
      () => {
        setSaving(false);
        setRenaming(null);
        reload();
      },
      undefined,
      { onError: () => setSaving(false) },
    );
  };

  /** What the confirmation says, which differs for one model, several, and any still in use. */
  const deleteMessage = (() => {
    const inUse = deleting.filter(row => row.assignmentCount > 0);
    if (deleting.length === 1) {
      const [row] = deleting;
      return row.assignmentCount > 0
        ? tr('CalendarLayout.deleteAssignedWarning', '“{{name}}” is applied to {{count}} scope(s). Deleting it removes those assignments, and each scope falls back to whatever it inherits. This cannot be undone.', {
            name: row.name,
            count: row.assignmentCount,
          })
        : tr('CalendarLayout.deleteConfirm', 'Delete “{{name}}”? This cannot be undone.', { name: row.name });
    }
    return inUse.length > 0
      ? tr('CalendarLayout.deleteManyAssignedWarning', 'Delete {{count}} models? {{inUse}} of them are still applied somewhere — those assignments are removed too, and the scopes fall back to what they inherit. This cannot be undone.', {
          count: deleting.length,
          inUse: inUse.length,
        })
      : tr('CalendarLayout.deleteManyConfirm', 'Delete {{count}} models? This cannot be undone.', { count: deleting.length });
  })();

  const columnsConfig: ColumnConfig<CalendarLayoutModelRowOutDTO>[] = [
    {
      id: 'name',
      header: 'CalendarLayout.column.name',
      headerDefault: 'Model',
      width: '26%',
      sortable: false,
      order: 1,
      verticalAlign: 'middle',
      render: row => (
        <div className="flex flex-col gap-0.5">
          <div className="flex flex-row items-center gap-2">
            {/* Always a link, editable or not: a read-only model still has a page worth opening to
                see how it is laid out, and the name is what people aim at. */}
            <Link to={modelHref(row)} className="font-bold text-content-brand">
              {row.name}
            </Link>
            {currentDefault?.modelId === row.id && (
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-content-brand text-white">
                {tr('CalendarLayout.isDefault', 'Default')}
              </span>
            )}
          </div>
          {row.description && <span className="text-xs text-content-secondary">{row.description}</span>}
        </div>
      ),
    },
    {
      id: 'owner',
      header: 'CalendarLayout.column.owner',
      headerDefault: 'Owner',
      width: '16%',
      sortable: false,
      order: 2,
      verticalAlign: 'middle',
      render: row =>
        row.systemWide ? (
          <span className="text-xs px-2 py-0.5 rounded-full bg-content-brand/10 text-content-brand">{tr('CalendarLayout.systemWide', 'System-wide')}</span>
        ) : (
          <span className="text-sm">{row.ownerCompanyName ?? '—'}</span>
        ),
    },
    {
      id: 'state',
      header: 'CalendarLayout.column.state',
      headerDefault: 'State',
      width: '16%',
      sortable: false,
      order: 3,
      verticalAlign: 'middle',
      render: row => (
        <div className="flex flex-col text-xs">
          <span>{row.published ? tr('CalendarLayout.published', 'Published') : tr('CalendarLayout.notPublished', 'Not published')}</span>
          {row.hasDraft && <span className="text-complementary-warning">{tr('CalendarLayout.draftPending', 'Draft pending')}</span>}
          {row.calendarLayoutStatus === 'ARCHIVED' && <span className="text-content-tertiary">{tr('CalendarLayout.archived', 'Archived')}</span>}
        </div>
      ),
    },
    {
      id: 'assignments',
      header: 'CalendarLayout.column.assignments',
      headerDefault: 'Applied to',
      width: '12%',
      sortable: false,
      order: 4,
      verticalAlign: 'middle',
      render: row => <span className="text-sm">{row.assignmentCount}</span>,
    },
    {
      id: 'actions',
      header: 'CalendarLayout.column.actions',
      headerDefault: '',
      width: '18%',
      sortable: false,
      order: 5,
      verticalAlign: 'middle',
      render: row => {
        // The backend says who may change what; a customer sees system-wide models but owns none.
        const mine = row.editable;
        return (
          <div className="flex flex-row items-center gap-1">
            {/* Opening the model is the name link now; what is left in the row is the one action
                people come here for, with the rest behind the more menu as elsewhere. */}
            <RingilButton label={tr('CalendarLayout.applyTo', 'Apply to…')} mode="filled-rectangle" size="small" onClick={() => setApplying(row)} />
            <CalendarLayoutModelMenu
              buttonMode="table"
              dataTestId={`calendar-layout-model-more-${row.id}`}
              editable={mine}
              admin={admin}
              published={row.published}
              archived={row.calendarLayoutStatus === 'ARCHIVED'}
              isDefault={currentDefault?.modelId === row.id}
              onDuplicate={() => openDuplicate(row)}
              onRename={() => setRenaming({ ...row })}
              onSetDefault={() => setDefault({ modelId: row.id }, data => setCurrentDefault(data))}
              onArchive={() => archiveModels([row])}
              onDelete={() => setDeleting([row])}
            />
          </div>
        );
      },
    },
  ];

  const massActionConfig: MassActionConfig<CalendarLayoutModelRowOutDTO>[] = [
    {
      label: tr('CalendarLayout.archiveSelected', 'Archive selected'),
      onClick: () => archiveModels((tableRef.current?.selectedObjects ?? []).filter(row => row.editable && row.calendarLayoutStatus !== 'ARCHIVED')),
      disabled: selected => selected.every(row => !row.editable || row.calendarLayoutStatus === 'ARCHIVED'),
    },
    {
      label: tr('CalendarLayout.deleteSelected', 'Delete selected'),
      onClick: () => setDeleting((tableRef.current?.selectedObjects ?? []).filter(row => row.editable)),
      disabled: selected => selected.every(row => !row.editable),
    },
  ];

  const filtersConf: FilterConfig[] = [
    // Owner company leads: it is the coarsest cut through the list, and the one an admin reaches
    // for first. Admin only — a customer sees its own models plus the system-wide ones, so there is
    // nothing to narrow by.
    ...(admin
      ? [
          {
            filterType: FilterType.Dropdown,
            placeholder: tr('CalendarLayout.filter.ownerCompany', 'Owner company'),
            value: ['ownerCompanyId'],
            options: companyOptions,
          } as FilterConfig,
        ]
      : []),
    // No free-text `q` here: on a list of models it duplicated the name filter, and its "company or
    // location" half only means something on the assignments tab.
    { filterType: FilterType.InputText, placeholder: tr('CalendarLayout.filter.name', 'Model name'), value: ['name'] },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('CalendarLayout.filter.systemWide', 'Origin'),
      value: ['systemWide'],
      options: [
        { label: tr('CalendarLayout.filter.systemWideOnly', 'System-wide only'), value: 'true', active: true },
        { label: tr('CalendarLayout.filter.companyOnly', 'Company models only'), value: 'false', active: true },
      ],
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('CalendarLayout.filter.applied', 'Applied'),
      value: ['applied'],
      options: [
        { label: tr('CalendarLayout.filter.appliedYes', 'In use'), value: 'true', active: true },
        { label: tr('CalendarLayout.filter.appliedNo', 'Not used anywhere'), value: 'false', active: true },
      ],
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('CalendarLayout.filter.status', 'Status'),
      value: ['calendarLayoutStatus'],
      options: [
        { label: tr('CalendarLayout.filter.statusActive', 'Active'), value: 'ACTIVE', active: true },
        { label: tr('CalendarLayout.filter.statusArchived', 'Archived'), value: 'ARCHIVED', active: true },
        { label: tr('CalendarLayout.filter.statusAll', 'All'), value: 'ALL', active: true },
      ],
    },
  ];

  /**
   * Dropdown options carry string values (the filter bar's `OptionType` has no other kind), but the
   * API takes real booleans — an unconverted `"false"` would read as "true" on the wire.
   */
  const normalizeFilter = (filter: CalendarLayoutModelFilter): CalendarLayoutModelFilter => {
    const asBoolean = (value: unknown) => (value === undefined || value === null || value === '' ? undefined : value === true || value === 'true');
    return { ...filter, systemWide: asBoolean(filter.systemWide), applied: asBoolean(filter.applied) };
  };

  return (
    <div className="flex flex-col gap-3">
      {admin && (
        <div className="text-xs text-content-secondary">
          {currentDefault
            ? tr('CalendarLayout.defaultIs', 'System default: {{name}}. Every company without its own layout renders this one.', { name: currentDefault.modelName })
            : tr('CalendarLayout.noDefault', 'No system default is set — companies without a layout fall back to the built-in card.')}
        </div>
      )}

      <RingilDataTable
        key={refreshToken}
        ref={tableRef}
        columnConfig={columnsConfig as ColumnConfig<unknown>[]}
        loadDataFromApi={loadData}
        filterName={admin ? 'admin_calendar_layout_models' : 'calendar_layout_models'}
        filtersConf={filtersConf}
        normalizeFilter={normalizeFilter}
        massActionConfig={massActionConfig as MassActionConfig<unknown>[]}
        // A model the caller may not delete has nothing selectable about it, and letting it into
        // "select all" would inflate the count the confirmation reports.
        isRowSelectable={row => !!(row as CalendarLayoutModelRowOutDTO).editable}
      />

      {/* Create / duplicate */}
      <RingilDialog
        visible={form !== null}
        onHide={() => setForm(null)}
        heading={form?.cloneOf ? tr('CalendarLayout.duplicateModel', 'Duplicate model') : tr('CalendarLayout.createModel', 'New model')}
        buttons={
          <>
            <RingilButton label={tr('CalendarLayout.create', 'Create')} mode="filled" disabled={saving || !form?.name.trim()} onClick={submitForm} />
            <RingilButton label={tr('CalendarLayout.cancel', 'Cancel')} mode="outlined" onClick={() => setForm(null)} />
          </>
        }
      >
        <div className="flex flex-col gap-3 w-[480px]">
          {form?.cloneOf && (
            <div className="text-xs text-content-secondary">
              {tr('CalendarLayout.duplicateHint', 'Starts from the current layout of “{{name}}”. The original is left untouched.', { name: form.cloneOf.name })}
            </div>
          )}
          <Field label={tr('CalendarLayout.modelName', 'Name')} required={!form?.name.trim()}>
            <RingilInputText value={form?.name ?? ''} onChange={value => setForm(previous => previous && { ...previous, name: value })} fullWidth />
          </Field>
          <Field label={tr('CalendarLayout.modelDescription', 'Description')}>
            <InputTextarea
              value={form?.description ?? ''}
              onChange={e => setForm(previous => previous && { ...previous, description: e.target.value })}
              rows={3}
              className="w-full"
            />
          </Field>
          {admin && (
            <Field label={tr('CalendarLayout.owner', 'Owner')}>
              <Dropdown
                value={form?.ownerCompanyId ?? null}
                options={companyOptions}
                onChange={e => setForm(previous => previous && { ...previous, ownerCompanyId: e.value ?? null })}
                filter
                showClear
                placeholder={tr('CalendarLayout.ownerSystemWide', 'System-wide — every company can use it')}
                className="w-full min-w-0"
                panelClassName={PICKER_PANEL_CLASS}
                itemTemplate={wrappingItemTemplate}
                valueTemplate={truncatedValueTemplate(tr('CalendarLayout.ownerSystemWide', 'System-wide — every company can use it'))}
              />
            </Field>
          )}
          {admin && (
            <div className="text-xs text-content-secondary">
              {form?.ownerCompanyId
                ? tr('CalendarLayout.ownerCompanyNote', 'Only this company sees the model, and only it can be assigned to that company’s locations.')
                : tr('CalendarLayout.systemWideNote', 'Offered to every company, editable only by admins.')}
            </div>
          )}
        </div>
      </RingilDialog>

      {/* Rename */}
      <RingilDialog
        visible={renaming !== null}
        onHide={() => setRenaming(null)}
        heading={tr('CalendarLayout.rename', 'Rename')}
        buttons={
          <>
            <RingilButton label={tr('CalendarLayout.save', 'Save')} mode="filled" disabled={saving || !renaming?.name.trim()} onClick={submitRename} />
            <RingilButton label={tr('CalendarLayout.cancel', 'Cancel')} mode="outlined" onClick={() => setRenaming(null)} />
          </>
        }
      >
        <div className="flex flex-col gap-3 w-[480px]">
          <Field label={tr('CalendarLayout.modelName', 'Name')} required={!renaming?.name.trim()}>
            <RingilInputText value={renaming?.name ?? ''} onChange={value => setRenaming(previous => previous && { ...previous, name: value })} fullWidth />
          </Field>
          <Field label={tr('CalendarLayout.modelDescription', 'Description')}>
            <InputTextarea
              value={renaming?.description ?? ''}
              onChange={e => setRenaming(previous => previous && { ...previous, description: e.target.value })}
              rows={3}
              className="w-full"
            />
          </Field>
        </div>
      </RingilDialog>

      {/* Delete — destructive and not undoable, so it is confirmed rather than done on the click. */}
      <ConfirmDialog
        visible={deleting.length > 0}
        dialogTitle={tr('CalendarLayout.deleteHeading', 'Delete layout model')}
        dialogMessage={deleteMessage}
        onAcceptButtonText={tr('CalendarLayout.delete', 'Delete')}
        onCancelButtonText={tr('CalendarLayout.cancel', 'Cancel')}
        loading={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting([])}
      />

      {/* Apply this model to a company / specific locations */}
      <ApplyModelDialog
        model={applying}
        admin={admin}
        onHide={() => setApplying(null)}
        onApplied={results => {
          setApplyResults(results);
          reload();
          onChanged?.();
        }}
      />

      {/* Per-target outcome — with replaceExisting off, a partial skip is the normal result. */}
      <RingilDialog
        visible={applyResults !== null}
        onHide={() => setApplyResults(null)}
        heading={tr('CalendarLayout.assignResults', 'Result')}
        buttons={<RingilButton label={tr('CalendarLayout.close', 'Close')} mode="filled" onClick={() => setApplyResults(null)} />}
      >
        <div className="flex flex-col gap-1 w-[520px] max-h-[60vh] overflow-y-auto text-xs">
          {(applyResults?.results ?? []).map((result, index) => (
            <div
              key={`${result.companyId}:${result.locationId ?? ''}:${index}`}
              className="flex flex-row justify-between gap-3 border-b border-0 border-solid border-content-quaternary py-1"
            >
              <span>{[result.companyId, result.locationId].filter(Boolean).join(' — ')}</span>
              <span className={result.calendarLayoutOutcome === 'SKIPPED' ? 'text-content-tertiary' : 'text-content-primary'}>
                {result.calendarLayoutOutcome}
                {result.reason ? ` — ${result.reason}` : ''}
              </span>
            </div>
          ))}
        </div>
      </RingilDialog>

      {/* What the bulk delete / archive actually did. Worth showing rather than a green toast:
          every affected scope silently starts rendering something else. */}
      <RingilDialog
        visible={impact !== null}
        onHide={() => setImpact(null)}
        heading={impact?.kind === 'delete' ? tr('CalendarLayout.deleted', 'Deleted') : tr('CalendarLayout.archived', 'Archived')}
        buttons={<RingilButton label={tr('CalendarLayout.close', 'Close')} mode="filled" onClick={() => setImpact(null)} />}
      >
        <div className="flex flex-col gap-3 w-[560px] max-h-[60vh] overflow-y-auto">
          <div className="text-sm">
            {impact?.kind === 'delete'
              ? tr('CalendarLayout.deleteImpactSummary', '{{count}} model(s) deleted.', { count: impact?.results.length ?? 0 })
              : tr('CalendarLayout.archiveImpactSummary', '{{count}} model(s) archived.', { count: impact?.results.length ?? 0 })}
          </div>

          {(impact?.results ?? []).map(result => {
            const affected = result.impact?.affected ?? [];
            const removed = 'removedAssignmentCount' in result ? result.removedAssignmentCount : undefined;
            return (
              <div key={result.modelId} className="flex flex-col gap-1">
                <div className="text-sm font-bold">{result.modelName}</div>
                <div className="text-xs text-content-secondary">
                  {removed !== undefined
                    ? tr('CalendarLayout.removedAssignments', '{{count}} assignment(s) removed', { count: removed })
                    : tr('CalendarLayout.affectedAssignments', '{{count}} assignment(s) affected', { count: result.impact?.assignmentCount ?? 0 })}
                </div>
                {affected.map(row => (
                  <div key={row.assignmentId} className="text-xs text-content-secondary border-b border-0 border-solid border-content-quaternary py-1">
                    {[row.companyName, row.locationName].filter(Boolean).join(' — ') || row.calendarLayoutScopeType}
                    {row.fallsBackToModelName
                      ? ` → ${row.fallsBackToModelName}`
                      : ` → ${tr('CalendarLayout.fallsBackToBuiltIn', 'built-in layout')}`}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </RingilDialog>
    </div>
  );
};
